import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * How long a load may take before it is worth telling the user about.
 *
 * Below this, showing a placeholder makes the application feel *slower*: the
 * eye registers the flash of grey bars and the layout settling, which reads as
 * work, where an unannounced 90ms swap reads as instant. Above it, showing
 * nothing reads as a dead click — except that it no longer does, because
 * {@link RouteProgress} answers the click at 90ms with a bar in the chrome. The
 * placeholder is now the second thing said, not the first, which is what buys
 * the extra 40ms: a screen that answers a local read in a quarter of a second
 * should never paint grey bars at all.
 *
 * The screens this covers are mostly local reads that return in tens of
 * milliseconds, so in practice the placeholder never appears at all — which is
 * the intended outcome, not a missing feature.
 */
const SKELETON_DELAY_MS = 220;

/**
 * True once `active` has been continuously true for `delay` ms.
 *
 * Exported because a screen with its own placeholder wants the same timing —
 * two loading indicators appearing at different moments is worse than either
 * appearing alone.
 */
export function useDelayed(active: boolean, delay = SKELETON_DELAY_MS): boolean {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [active, delay]);

  return shown;
}

/**
 * One placeholder bar.
 *
 * It breathes rather than shimmers, and the animation is deliberately **not**
 * staggered: every bar on the screen shares one 1.8s cycle, so a loading grid
 * pulses as a single object instead of thirty independent ones. The swept
 * gradient this replaced did the opposite — out of phase across a full page it
 * read as visual noise, and it moved faster than the data it was standing in
 * for, which is a promise the network does not keep.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('rounded-[6px] bg-[var(--line-2)]', className)}
      style={{ animation: 'bb-skeleton 1.8s ease-in-out infinite' }}
    />
  );
}

/**
 * The indeterminate bar that answers a nav click.
 *
 * **This is the first thing the application says**, and the reason the grey
 * placeholders can afford to wait. A click on the rail used to produce nothing
 * for as long as the API took and then a screenful of shimmer arriving at once;
 * the bar turns that into one small acknowledgement in the chrome, in a place
 * that never reflows the page, followed by the content itself.
 *
 * Ninety milliseconds of silence first, because a screen whose data is already
 * in hand should show no loading state at all — and the bar is absolutely
 * positioned so that appearing and vanishing moves nothing under the cursor.
 *
 * `role="progressbar"` with no value: honest, since nothing here knows how far
 * along a request is. Screen readers get the `aria-label`; the animation is
 * decoration and `prefers-reduced-motion` flattens it like every other.
 */
export function RouteProgress({ active, className }: { active: boolean; className?: string }) {
  const shown = useDelayed(active, 90);

  if (!shown) return null;

  return (
    <div
      role="progressbar"
      aria-label="Loading"
      data-noprint="1"
      className={cn(
        'bb-fade-in pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden',
        className,
      )}
    >
      <div
        className="h-full w-full bg-[var(--accent)]"
        style={{ animation: 'bb-progress 1.1s var(--ease) infinite' }}
      />
    </div>
  );
}

/**
 * Placeholder rows matching the grid's density, so nothing shifts on load.
 *
 * **Nothing is painted for the first {@link SKELETON_DELAY_MS}.** Every call
 * site renders this the instant its query starts, and most of those queries
 * finish in well under that — so without the delay the common path was a
 * shimmer that appeared and vanished inside a couple of frames on every click
 * of the nav rail. That flash is what made the application feel unfinished; the
 * fix is to say nothing until saying something is warranted.
 *
 * The height is reserved either way: the wrapper keeps the rows' space while
 * the placeholder is invisible, so content arriving before the delay elapses
 * does not push the page around. Then it fades rather than snapping in, which
 * `prefers-reduced-motion` reduces to nothing, as with every animation here.
 */
export function SkeletonRows({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  const shown = useDelayed(true);

  if (!shown) return <div aria-hidden="true" className="flex flex-col" style={{ opacity: 0 }} />;

  return (
    <div className="bb-fade-in flex flex-col">
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="bb-row-h flex items-center gap-4 border-b border-[var(--line-2)] px-[var(--pad)]"
        >
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton key={c} className={cn('h-3', c === 2 ? 'flex-1' : 'w-20')} />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
    >
      {icon && <div className="text-[var(--ink-3)]">{icon}</div>}
      <div>
        <p className="m-0 text-[14px] font-medium text-[var(--ink)]">{title}</p>
        {description && (
          <p className="mx-auto mt-1.5 max-w-[52ch] text-[12.5px] text-[var(--ink-2)]">
            {description}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

export type CalloutTone = 'info' | 'warn' | 'danger' | 'success';

const CALLOUT: Record<CalloutTone, string> = {
  info: 'border-[var(--line)] bg-[var(--surface-2)] text-[var(--ink-2)]',
  warn: 'border-[var(--warn)]/30 bg-[var(--warn-soft)] text-[var(--warn)]',
  danger: 'border-[var(--debit)]/30 bg-[var(--debit-soft)] text-[var(--debit)]',
  success: 'border-[var(--credit)]/30 bg-[var(--accent-soft)] text-[var(--credit)]',
};

/**
 * Inline message. Used for the things this application must say clearly:
 * "Period 06 is closed", "Credit limit exceeded by PKR 412,000",
 * "This voucher has been reversed by JV-26-000418".
 */
export function Callout({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-[var(--radius)] border px-3.5 py-2.5 text-[12.5px]',
        CALLOUT[tone],
        className,
      )}
    >
      {title && <div className="font-medium">{title}</div>}
      {children && <div className={cn(title && 'pt-1 opacity-90')}>{children}</div>}
    </div>
  );
}

/** Live status dot — the FX ticker's pulse in the top bar. */
export function PulseDot({
  tone = 'ok',
  className,
}: {
  tone?: 'ok' | 'warn' | 'bad';
  className?: string;
}) {
  const colour = tone === 'ok' ? 'var(--credit)' : tone === 'warn' ? 'var(--warn)' : 'var(--debit)';
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-1.5 rounded-full', className)}
      style={{ background: colour, animation: 'bb-pulse-dot 2.4s ease-in-out infinite' }}
    />
  );
}
